import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Clock, Mail, FileText, MessageSquare, Calendar, User,
  CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp,
  File, Phone, Video, Edit, Plus, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { cn } from '../../lib/utils';
import { format, parseISO, isToday, isYesterday, isThisWeek, startOfDay } from 'date-fns';
import { nb } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CASE TIMELINE MODAL
 * 
 * Shows a chronological history of everything that happened in a case:
 * - Emails sent/received
 * - Documents uploaded
 * - Tasks created/completed
 * - Status changes
 * - Meetings/events
 * - Notes/activities
 */
const CaseTimelineModal = ({ isOpen, onClose, caseId, caseName }: { isOpen: boolean; onClose: () => void; caseId: string; caseName?: string }) => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Fetch timeline data
  const fetchTimeline = useCallback(async () => {
    if (!caseId || !isOpen) return;
    
    setLoading(true);
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all relevant data in parallel
      const [messagesRes, docsRes, tasksRes, caseRes] = await Promise.all([
        axios.get(`${API}/inbox/messages?case_id=${caseId}&limit=100`, { headers }).catch(() => ({ data: { messages: [] } })),
        axios.get(`${API}/documents/case/${caseId}`, { headers }).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/tasks?case_id=${caseId}`, { headers }).catch(() => ({ data: { tasks: [] } })),
        axios.get(`${API}/cases/${caseId}`, { headers }).catch(() => ({ data: null }))
      ]);
      
      const allEvents = [];
      
      // Process emails
      (messagesRes.data?.messages || []).forEach(msg => {
        allEvents.push({
          id: msg.id,
          type: 'email',
          title: msg.subject || 'Uten emne',
          description: msg.from?.name || msg.from?.email || 'Ukjent avsender',
          timestamp: msg.created_at || msg.receivedDateTime,
          icon: Mail,
          color: 'blue',
          metadata: {
            direction: msg.folder === 'sentitems' ? 'sent' : 'received',
            from: msg.from,
            to: msg.to
          }
        });
      });
      
      // Process documents
      (docsRes.data?.documents || []).forEach(doc => {
        allEvents.push({
          id: doc.id,
          type: 'document',
          title: doc.filename || 'Dokument',
          description: `Lastet opp av ${doc.uploaded_by_name || 'ukjent'}`,
          timestamp: doc.uploaded_at || doc.created_at,
          icon: FileText,
          color: 'amber',
          metadata: {
            fileType: doc.file_type,
            size: doc.file_size
          }
        });
      });
      
      // Process tasks
      (tasksRes.data?.tasks || tasksRes.data || []).forEach(task => {
        allEvents.push({
          id: task.id,
          type: 'task',
          title: task.title,
          description: task.status === 'DONE' ? 'Fullført' : 'Opprettet',
          timestamp: task.completed_at || task.created_at,
          icon: task.status === 'DONE' ? CheckCircle : Plus,
          color: task.status === 'DONE' ? 'green' : 'slate',
          metadata: {
            status: task.status,
            priority: task.priority
          }
        });
      });
      
      // Add case creation event
      if (caseRes.data?.created_at) {
        allEvents.push({
          id: `case-created-${caseId}`,
          type: 'case',
          title: 'Sak opprettet',
          description: caseName || caseRes.data.title,
          timestamp: caseRes.data.created_at,
          icon: Plus,
          color: 'purple',
          metadata: {}
        });
      }
      
      // Sort by timestamp (newest first)
      allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setEvents(allEvents);
      
      // Auto-expand today's group
      setExpandedGroups({ 'I dag': true, 'I går': true });
      
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoading(false);
    }
  }, [caseId, caseName, isOpen]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // Filter events
  const filteredEvents = events.filter(e => {
    if (filter === 'all') return true;
    return e.type === filter;
  });

  // Group events by date
  const groupedEvents = filteredEvents.reduce((groups, event) => {
    const date = parseISO(event.timestamp);
    let groupKey;
    
    if (isToday(date)) {
      groupKey = 'I dag';
    } else if (isYesterday(date)) {
      groupKey = 'I går';
    } else if (isThisWeek(date)) {
      groupKey = format(date, 'EEEE', { locale: nb }); // Day name
    } else {
      groupKey = format(date, 'MMMM yyyy', { locale: nb }); // Month Year
    }
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(event);
    return groups;
  }, {});

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'blue': return { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' };
      case 'amber': return { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' };
      case 'green': return { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' };
      case 'purple': return { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Sakshistorikk</h2>
              <p className="text-sm text-gray-500">{caseName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 mt-4">
            {[
              { id: 'all', label: 'Alt', count: events.length },
              { id: 'email', label: 'E-post', count: events.filter(e => e.type === 'email').length },
              { id: 'document', label: 'Dokumenter', count: events.filter(e => e.type === 'document').length },
              { id: 'task', label: 'Oppgaver', count: events.filter(e => e.type === 'task').length }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                  filter === f.id
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                )}
              >
                {f.label}
                <span className="ml-1.5 text-xs opacity-60">({f.count})</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Clock className="w-12 h-12 mb-3 text-gray-300" />
              <p>Ingen hendelser funnet</p>
            </div>
          ) : (
            <div className="py-4">
              {Object.entries(groupedEvents).map(([groupKey, groupEvents]) => (
                <div key={groupKey} className="mb-2">
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center gap-2 px-6 py-2 text-left hover:bg-gray-50 transition-colors"
                  >
                    {expandedGroups[groupKey] ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-sm font-medium text-gray-700">{groupKey}</span>
                    <span className="text-xs text-gray-400">({(groupEvents as any[]).length})</span>
                  </button>
                  
                  {/* Group content */}
                  {expandedGroups[groupKey] && (
                    <div className="relative pl-10 pr-6">
                      {/* Timeline line */}
                      <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-200" />
                      
                      {(groupEvents as any[]).map((event: any, idx: number) => {
                        const colors = getColorClasses(event.color);
                        const Icon = event.icon;
                        
                        return (
                          <div key={event.id} className="relative pb-4">
                            {/* Timeline dot */}
                            <div className={cn(
                              "absolute -left-5 w-4 h-4 rounded-full flex items-center justify-center",
                              colors.bg, colors.border, "border-2"
                            )}>
                              <div className={cn("w-1.5 h-1.5 rounded-full", colors.text.replace('text-', 'bg-'))} />
                            </div>
                            
                            {/* Event card */}
                            <div className="ml-4 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                              <div className="flex items-start gap-3">
                                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", colors.bg)}>
                                  <Icon className={cn("w-4 h-4", colors.text)} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {event.title}
                                  </p>
                                  <p className="text-xs text-gray-500">{event.description}</p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {format(parseISO(event.timestamp), 'HH:mm', { locale: nb })}
                                    {event.type === 'email' && event.metadata?.direction && (
                                      <span className="ml-2">
                                        {event.metadata.direction === 'sent' ? '→ Sendt' : '← Mottatt'}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer with stats */}
        <div className="flex-shrink-0 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Totalt {events.length} hendelser</span>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4 text-blue-500" />
                {events.filter(e => e.type === 'email').length}
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4 text-amber-500" />
                {events.filter(e => e.type === 'document').length}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {events.filter(e => e.type === 'task').length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseTimelineModal;
